/**
 * SQL Ingestion Types
 * Types for database connections, jobs, and job history
 */

export type DatabaseType = 'postgresql' | 'mysql' | 'sqlserver';

export interface SSHConfig {
  host: string;
  port: number;
  username: string;
}

export interface SQLConnection {
  connection_id: string;  // DynamoDB field name
  user_id: string;
  name: string;
  type: DatabaseType;
  host: string;
  port: number;
  database: string;
  username: string;
  secretArn: string;
  useVpc: boolean;
  vpcConfig?: {
    subnetIds: string[];
    securityGroupIds: string[];
  };
  useSshTunnel: boolean;
  sshConfig?: SSHConfig;
  createdAt: string;
  updatedAt: string;
}

export interface SSHConfigInput {
  host: string;
  port?: number;
  username: string;
  privateKey: string;  // PEM format private key
}

export interface SQLConnectionInput {
  name: string;
  type: DatabaseType;
  host: string;
  port?: number;
  database: string;
  username: string;
  password: string;
  useVpc?: boolean;
  vpcConfig?: {
    subnetIds: string[];
    securityGroupIds: string[];
  };
  useSshTunnel?: boolean;
  sshConfig?: SSHConfigInput;
}

export interface SQLJob {
  job_id: string;  // Note: backend returns snake_case
  user_id: string;
  name: string;
  connectionId: string;
  connectionName: string;
  query: string;
  schedule: string | null;
  eventBridgeRule: string | null;
  status: 'active' | 'paused' | 'disabled';
  lastRunAt?: string;
  lastRunStatus?: 'success' | 'failed' | 'running';
  createdAt: string;
  updatedAt: string;
}

export interface SQLJobInput {
  name: string;
  connectionId: string;
  query: string;
  schedule?: string | null;
}

export interface SQLJobRun {
  runId: string;
  jobId: string;
  user_id: string;
  jobName?: string;
  startedAt: string;
  completedAt?: string;
  status: 'success' | 'failed' | 'running';
  rowsIngested?: number;
  outputFileKey?: string;
  fileSizeBytes?: number;
  durationSeconds?: number;
  errorMessage?: string;
  logFileKey?: string;
  downloadUrl?: string;
}

export interface SQLTestResult {
  success: boolean;
  message?: string;
  serverVersion?: string;
  error?: string;
  details?: string;
}

export interface SQLQueryTestResult {
  success: boolean;
  columns?: string[];
  rows?: Record<string, any>[];
  rowCount?: number;
  message?: string;
  error?: string;
  details?: string;
}

// API Response types
export interface SQLConnectionsResponse {
  success: boolean;
  connections?: SQLConnection[];
  connection?: SQLConnection;
  count?: number;
  message?: string;
  error?: string;
}

export interface SQLJobsResponse {
  success: boolean;
  jobs?: SQLJob[];
  job?: SQLJob;
  count?: number;
  message?: string;
  error?: string;
}

export interface SQLJobHistoryResponse {
  success: boolean;
  jobId?: string;
  jobName?: string;
  runs?: SQLJobRun[];
  run?: SQLJobRun;
  count?: number;
  error?: string;
}

export interface SQLJobRunResponse {
  success: boolean;
  run_id?: string;
  rows_ingested?: number;
  output_file?: string;
  file_size_bytes?: number;
  duration_seconds?: number;
  error?: string;
}

export interface SQLLogsResponse {
  success: boolean;
  runId?: string;
  logs?: string;
  note?: string;
  error?: string;
}

// Schedule presets
export const SCHEDULE_PRESETS = [
  { label: 'Manual (no schedule)', value: '' },
  { label: 'Every 15 minutes', value: 'rate(15 minutes)' },
  { label: 'Every 30 minutes', value: 'rate(30 minutes)' },
  { label: 'Hourly', value: 'rate(1 hour)' },
  { label: 'Every 6 hours', value: 'rate(6 hours)' },
  { label: 'Every 12 hours', value: 'rate(12 hours)' },
  { label: 'Daily', value: 'rate(1 day)' },
  { label: 'Weekly', value: 'rate(7 days)' },
] as const;

// Database type info
export const DATABASE_TYPES: { type: DatabaseType; label: string; defaultPort: number; icon: string }[] = [
  { type: 'postgresql', label: 'PostgreSQL', defaultPort: 5432, icon: '🐘' },
  { type: 'mysql', label: 'MySQL', defaultPort: 3306, icon: '🐬' },
  { type: 'sqlserver', label: 'SQL Server', defaultPort: 1433, icon: '🗄️' },
];
